import { Button, Text } from '@ebh/capsule';
import { NoSearchIcon } from '@ebh/capsule/icons';
import styles from './styles';

interface NotFoundProps {
  title?: string;
  description?: string;
  buttonText?: string;
  onClick?: () => void;
  showButton?: boolean;
  variant?: 'page' | 'search' | 'error' | 'empty';
}

const getDefaultContent = (variant: string) => {
  switch (variant) {
    case 'page':
      return {
        title: 'Page Not Found',
        description:
          "The page you're looking for doesn't exist or has been moved. Please check the URL or navigate back to the homepage.",
        buttonText: 'Go to Homepage',
      };
    case 'search':
      return {
        title: 'No Results Found',
        description:
          "We couldn't find any results matching your search criteria. Try adjusting your filters or search terms.",
        buttonText: 'Clear Filters',
      };
    case 'error':
      return {
        title: 'Something Went Wrong',
        description:
          'We encountered an unexpected error. Please try again or contact support if the problem persists.',
        buttonText: 'Try Again',
      };
    case 'empty':
      return {
        title: 'Nothing Here Yet',
        description:
          'This section is empty. Start by adding some content or check back later for updates.',
        buttonText: 'Add Content',
      };
    default:
      return {
        title: 'Not Found',
        description: "The content you're looking for is not available.",
        buttonText: 'Go Back',
      };
  }
};

function NotFound({
  title,
  description,
  buttonText,
  onClick,
  showButton = true,
  variant = 'page',
}: NotFoundProps) {
  const defaultContent = getDefaultContent(variant);
  const finalTitle = title || defaultContent.title;
  const finalDescription = description || defaultContent.description;
  const finalButtonText = buttonText || defaultContent.buttonText;

  return (
    <div css={styles.container}>
      <div css={styles.body}>
        <div css={styles.noSearchIcon}>
          <NoSearchIcon scale={40} color="gray-400" />
        </div>

        <div css={styles.heading}>
          <Text variant="header-3" color="gray-500" align="center">
            {finalTitle}
          </Text>
          <Text variant="body-1" color="gray-500" align="center">
            {finalDescription}
          </Text>
        </div>
      </div>

      {showButton && onClick && (
        <div css={styles.buttonWrapper}>
          <Button variant="primary" onClick={onClick} css={styles.button}>
            {finalButtonText}
          </Button>
        </div>
      )}
    </div>
  );
}

export default NotFound;
